import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Container,
    Paper,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Lock as LockIcon, Email as EmailIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { clearAuthError } from '../../store';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const dispatch = useDispatch();
    const authError = useSelector((state: RootState) => state.auth.error);
    const authLoading = useSelector((state: RootState) => state.auth.loading);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        
        // Validate inputs
        if (!email || !password) {
            return false;
        }
        
        // Clear previous errors
        dispatch(clearAuthError());
        
        // Dispatch login - errors will be handled via Redux state
        login(email, password);
        
        return false;
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            p: 4,
                            textAlign: 'center',
                        }}
                    >
                        <Box
                            component="img"
                            src="/logo.png"
                            alt="Rise Strong Logo"
                            sx={{
                                height: 64,
                                width: 64,
                                mb: 2,
                                borderRadius: 2,
                            }}
                        />
                        <Typography variant="h4" component="h1" gutterBottom>
                            Rise Strong Admin
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Sign in to access the admin dashboard
                        </Typography>
                    </Box>

                    <CardContent sx={{ p: 4 }}>
                        {authError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {authError}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                margin="normal"
                                required
                                autoComplete="email"
                                InputProps={{
                                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                                }}
                                disabled={authLoading}
                            />

                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                margin="normal"
                                required
                                autoComplete="current-password"
                                InputProps={{
                                    startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                disabled={authLoading}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={authLoading || !email || !password}
                                sx={{
                                    mt: 3,
                                    mb: 2,
                                    py: 1.5,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #4338ca 0%, #7c3aed 100%)',
                                    },
                                    '&:disabled': {
                                        background: 'rgba(0, 0, 0, 0.12)',
                                    },
                                }}
                            >
                                {authLoading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Sign In'
                                )}
                            </Button>
                        </form>

                        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" color="textSecondary" align="center">
                                <strong>Admin Access Required</strong>
                                <br />
                                Only authorized administrators can access this dashboard.
                                <br />
                                Contact your system administrator for access.
                            </Typography>
                        </Box>
                    </CardContent>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
